import { OrderItem } from "../../interfaces/order.interface";
import { EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateText } from "../OrdersPage/style";
import { ShoppingCart, Pencil, Trash2, CreditCard } from "lucide-react";
import {
  PageWrapper, PageTitle, TableWrapper, Table,
  Thead, Tbody, Tfoot, Th, Tr, Td, TotalRow, TotalLabel, TotalValue,
  ActionCell, IconButton, CheckoutBar, CheckoutButton,
} from "./style";

interface OrderListProps {
  items: OrderItem[];
}

interface GroupedItem {
  productName: string;
  quantity: number;
  unitPrice: number | undefined;
  subtotal: number | undefined;
}

const groupItems = (items: OrderItem[]): GroupedItem[] => {
  const map = new Map<string, GroupedItem>();
  for (const item of items) {
    const existing = map.get(item.productName);
    const prevQty = existing === undefined ? 0 : existing.quantity;
    const quantity = prevQty + item.quantity;
    const unitPrice = existing?.unitPrice ?? item.unitPrice;
    map.set(item.productName, {
      productName: item.productName,
      quantity,
      unitPrice,
      subtotal: unitPrice === undefined ? undefined : quantity * unitPrice,
    });
  }
  return Array.from(map.values());
};

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const OrderList = ({ items }: OrderListProps) => {
  const grouped = groupItems(items);
  const hasPrices = grouped.some((item) => item.unitPrice !== undefined);
  const total = grouped.reduce<number>((sum, item) => sum + (item.subtotal ?? 0), 0);

  if (grouped.length === 0) {
    return (
      <EmptyState>
        <EmptyStateIcon>
          <ShoppingCart size={48} />
        </EmptyStateIcon>
        <EmptyStateTitle>Nenhum item no pedido</EmptyStateTitle>
        <EmptyStateText>Os itens aparecerão aqui assim que forem adicionados.</EmptyStateText>
      </EmptyState>
    );
  }

  const colSpan = (hasPrices ? 3 : 1) + 1;

  return (
    <PageWrapper>
      <PageTitle>Carrinho de Compras</PageTitle>
      <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Produto</Th>
              {hasPrices && <Th align="right">Preço unit.</Th>}
              <Th align="right">Qtd.</Th>
              {hasPrices && <Th align="right">Subtotal</Th>}
              <Th />
            </tr>
          </Thead>
          <Tbody>
            {grouped.map((item) => (
              <Tr key={item.productName}>
                <Td>{item.productName}</Td>
                {hasPrices && (
                  <Td align="right">
                    {item.unitPrice === undefined ? "—" : formatCurrency(item.unitPrice)}
                  </Td>
                )}
                <Td align="right">{item.quantity}</Td>
                {hasPrices && (
                  <Td align="right" bold accent>
                    {item.subtotal === undefined ? "—" : formatCurrency(item.subtotal)}
                  </Td>
                )}
                <ActionCell>
                  <IconButton variant="edit" title="Editar item">
                    <Pencil size={14} />
                  </IconButton>
                  <IconButton variant="remove" title="Remover item">
                    <Trash2 size={14} />
                  </IconButton>
                </ActionCell>
              </Tr>
            ))}
          </Tbody>
          {hasPrices && (
            <Tfoot>
              <TotalRow>
                <TotalLabel colSpan={colSpan}>Total</TotalLabel>
                <TotalValue>{formatCurrency(total)}</TotalValue>
              </TotalRow>
            </Tfoot>
          )}
        </Table>
      </TableWrapper>
      <CheckoutBar>
        <CheckoutButton>
          <CreditCard size={18} />
          Finalizar Compra
        </CheckoutButton>
      </CheckoutBar>
    </PageWrapper>
  );
};
